import React, { useEffect, useState } from "react";

export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["home", "about", "services", "board", "insights", "contact"];
      const scrollPosition = window.scrollY + 140;

      for (const id of sections) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.offsetTop;
        const bottom = top + el.offsetHeight;
        if (scrollPosition >= top && scrollPosition < bottom) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  const navItems = [
    { label: "Home", href: "#home", id: "home" },
    { label: "About", href: "#about", id: "about" },
    { label: "Services", href: "#services", id: "services" },
    { label: "Board/NED", href: "#board", id: "board" },
    { label: "Insights", href: "#insights", id: "insights" },
    { label: "Contact", href: "#contact", id: "contact" },
  ];

  return (
    <div style={styles.appShell}>
      <style>{globalCss}</style>

      <header className="site-header">
        <div className="container nav-wrap">
          <a href="#home" className="brand" onClick={closeMenu}>
            <div className="brand-title">Improve Associates Ltd</div>
            <div className="brand-subtitle">Making your best . . . even better</div>
          </a>

          <button
            className="menu-button"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle navigation"
            aria-expanded={menuOpen}
          >
            <span />
            <span />
            <span />
          </button>

          <nav className={`nav ${menuOpen ? "open" : ""}`}>
            {navItems.map((item) => (
              <a
                key={item.id}
                href={item.href}
                className={activeSection === item.id ? "active" : ""}
                onClick={closeMenu}
              >
                {item.label}
              </a>
            ))}
          </nav>
        </div>
      </header>

      <main>
        <section id="home" className="section hero-section light-panel">
          <div className="container hero-grid">
            <div>
              <div className="eyebrow">EXECUTIVE LEADERSHIP ADVISOR &amp; COACH</div>
              <h1 className="hero-title">
                High performance is expected.
                <br />
                <span>Trusted leadership is what sets you apart.</span>
              </h1>
              <p className="hero-copy">
                Executive coaching and strategic advisory support for CEOs, MDs, Board leaders,
                PE-backed operators and senior executives navigating growth, pressure, visibility,
                transition and high-stakes decisions.
              </p>
              <div className="hero-cta-row">
                <a href="#contact" className="btn btn-primary">Book a confidential conversation</a>
                <a href="#services" className="btn btn-secondary">Explore how I help leaders</a>
              </div>
            </div>

            <div className="hero-card">
              <div className="hero-card-top">Why senior leaders work with Derek McIntyre</div>
              <div className="trust-grid">
                <div className="trust-item">
                  <div className="stat">40+ years</div>
                  <p>International leadership experience across complex organisations and sectors.</p>
                </div>
                <div className="trust-item">
                  <div className="stat">Former COO</div>
                  <p>Commercial, operational and strategic leadership grounded in real accountability.</p>
                </div>
                <div className="trust-item">
                  <div className="stat">Board-aware</div>
                  <p>Governance perspective shaped by board, trustee and advisory experience.</p>
                </div>
                <div className="trust-item">
                  <div className="stat">Practical</div>
                  <p>Coaching that works in the real world of pressure, complexity and politics.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section">
          <div className="container">
            <div className="panel panel-large">
              <h2>When leaders typically reach out</h2>
              <p className="section-intro">
                Senior leaders rarely seek support because they lack capability. More often, they reach
                out because the stakes have increased, the context has become more political, or the next
                move matters more than ever.
              </p>
              <div className="two-col-grid">
                <div className="soft-card tinted">
                  <h3>What leaders are navigating</h3>
                  <ul className="tick-list">
                    <li>Stepping into a larger role with greater visibility and scrutiny</li>
                    <li>Political complexity increasing faster than support around them</li>
                    <li>Influencing stronger personalities, boards or wider stakeholders</li>
                    <li>Strong performance, but recognition weaker than it should be</li>
                    <li>Preparing for CEO, MD, Board or strategic transition</li>
                    <li>Carrying pressure without a trusted sounding board</li>
                  </ul>
                </div>
                <div className="soft-card">
                  <h3>What changes through our work</h3>
                  <ul className="tick-list">
                    <li>Stronger executive presence and calm authority</li>
                    <li>Clearer judgement under pressure and uncertainty</li>
                    <li>Sharper stakeholder influence and boardroom confidence</li>
                    <li>More control of reputation, positioning and narrative</li>
                    <li>Faster, stronger transitions into bigger roles</li>
                    <li>A place to think clearly, challenge assumptions and move decisively</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section light-surface">
          <div className="container">
            <div className="section-header centered">
              <div className="eyebrow">FREE EXECUTIVE LEADERSHIP RESOURCES</div>
              <h2>Practical tools for leaders navigating visibility, pressure and next-level responsibility</h2>
              <p>
                A short collection of executive worksheets designed to prompt reflection, sharpen judgement and
                help senior leaders assess where they may need stronger positioning, presence or support.
              </p>
            </div>

            <div className="resource-grid">
              <article className="resource-card">
                <div className="resource-tag">Executive Diagnostic</div>
                <h3>Performance vs Perception Audit</h3>
                <p>
                  Assess whether your contribution is being seen, understood and rewarded at the level it should be.
                </p>
                <a className="btn btn-secondary resource-btn" href="/Performance_vs_Perception_Audit_Improve_Associates.pdf" target="_blank" rel="noreferrer">
                  Download the Audit
                </a>
              </article>

              <article className="resource-card">
                <div className="resource-tag">Board / NED</div>
                <h3>Board Readiness Scorecard</h3>
                <p>
                  Evaluate your readiness for broader board contribution, governance perspective and non-executive opportunity.
                </p>
                <a className="btn btn-secondary resource-btn" href="/Board_Readiness_Scorecard_Improve_Associates.pdf" target="_blank" rel="noreferrer">
                  Download the Scorecard
                </a>
              </article>

              <article className="resource-card">
                <div className="resource-tag">Leadership Presence</div>
                <h3>Executive Presence Self-Assessment</h3>
                <p>
                  Explore how your clarity, composure, confidence and leadership presence may be experienced by others.
                </p>
                <a className="btn btn-secondary resource-btn" href="/Executive_Presence_Self_Assessment_Improve_Associates.pdf" target="_blank" rel="noreferrer">
                  Download the Assessment
                </a>
              </article>

              <article className="resource-card">
                <div className="resource-tag">CEO / MD Support</div>
                <h3>Trusted Advisor Diagnostic</h3>
                <p>
                  Measure whether you are operating as a trusted advisor or being seen primarily as a capable executor.
                </p>
                <a className="btn btn-secondary resource-btn" href="/Trusted_Advisor_Diagnostic_Improve_Associates.pdf" target="_blank" rel="noreferrer">
                  Download the Diagnostic
                </a>
              </article>
            </div>
          </div>
        </section>

        <section id="about" className="section">
          <div className="container">
            <div className="section-header">
              <div className="eyebrow">ABOUT DEREK</div>
              <h2>Derek McIntyre — Executive Leadership Advisor &amp; Coach</h2>
              <p>
                I work with senior leaders who need more than encouragement. They need a trusted, experienced
                partner who understands the reality of leadership when visibility is high, complexity is growing,
                and decisions carry real consequence.
              </p>
            </div>

            <div className="about-grid">
              <div className="panel">
                <h3>Why clients trust me</h3>
                <p>
                  My coaching is grounded in nearly four decades of international leadership experience across
                  semiconductor, electronics, mobile, automotive, medical and complex operational environments.
                  I have worked at senior level across the UK, Italy, Hungary, Austria, Mexico and the USA.
                </p>
                <p>
                  Before building my coaching practice, I held senior executive roles including COO and Global
                  Operations Director, leading through scale, pressure, transformation and accountability. That
                  matters because clients want support from someone who has sat where they sit.
                </p>
                <p>
                  Alongside executive coaching, I bring a strong governance perspective through board, trustee
                  and council roles. This helps leaders think not just about performance, but about reputation,
                  judgement, influence, decision quality and the expectations that come with senior responsibility.
                </p>
              </div>

              <div className="panel tinted-panel">
                <h3>What clients value most</h3>
                <ul className="tick-list">
                  <li>Commercial and operational credibility, not theory alone</li>
                  <li>Practical challenge that sharpens thinking and next-step judgement</li>
                  <li>Calm, trusted space to think clearly about difficult situations</li>
                  <li>Support with visibility, influence, reputation and stakeholder complexity</li>
                  <li>Strong understanding of board, governance and leadership dynamics</li>
                  <li>Coaching that respects pace, pressure and real-world accountability</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="services" className="section light-surface">
          <div className="container">
            <div className="section-header centered">
              <div className="eyebrow">SERVICES</div>
              <h2>Support designed for senior leaders and high-stakes moments</h2>
              <p>
                My work is tailored, confidential and grounded in the realities of senior leadership. While every
                engagement is bespoke, clients typically come to me in one of four areas.
              </p>
            </div>

            <div className="service-grid">
              <article className="service-card">
                <h3>Executive Leadership Coaching</h3>
                <p>
                  For senior leaders who are performing strongly but want to strengthen influence, visibility,
                  executive presence and strategic impact.
                </p>
                <ul className="mini-list">
                  <li>Executive presence and leadership identity</li>
                  <li>Influence across peers, functions and senior stakeholders</li>
                  <li>Pressure, confidence and judgement under scrutiny</li>
                </ul>
              </article>

              <article className="service-card">
                <h3>Strategic Transition Support</h3>
                <p>
                  For leaders stepping into broader roles, navigating promotion, succession, integration or major
                  organisational change.
                </p>
                <ul className="mini-list">
                  <li>Role transition and first-90-day thinking</li>
                  <li>Stakeholder alignment and political awareness</li>
                  <li>Sharper positioning in larger, more visible roles</li>
                </ul>
              </article>

              <article className="service-card">
                <h3>Board &amp; NED Readiness</h3>
                <p>
                  For executives preparing for board responsibility, non-executive opportunities or stronger board-level contribution.
                </p>
                <ul className="mini-list">
                  <li>Board presence, judgement and contribution</li>
                  <li>NED positioning, biography and readiness</li>
                  <li>Governance perspective and board-quality communication</li>
                </ul>
              </article>

              <article className="service-card">
                <h3>Trusted Advisor Support</h3>
                <p>
                  For CEOs, MDs and senior leaders who need a confidential, experienced external sounding board on complex issues.
                </p>
                <ul className="mini-list">
                  <li>Decision support on sensitive leadership situations</li>
                  <li>Handling conflict, challenge and stakeholder risk</li>
                  <li>Thinking partner support during high-pressure periods</li>
                </ul>
              </article>
            </div>
          </div>
        </section>

        <section id="board" className="section">
          <div className="container">
            <div className="section-header">
              <div className="eyebrow">BOARD / NED</div>
              <h2>Board-level judgement, presence and readiness</h2>
              <p>
                Board work requires a different level of judgement, contribution and visibility. Whether you are
                preparing for your first board role, strengthening your impact in the boardroom, or shaping a more
                credible NED profile, the challenge is rarely just technical. It is about perspective, confidence,
                contribution and trust.
              </p>
            </div>

            <div className="two-col-grid">
              <div className="panel">
                <h3>Typical areas of focus</h3>
                <ul className="tick-list">
                  <li>Preparing for first-time NED or board opportunities</li>
                  <li>Clarifying board contribution beyond executive delivery</li>
                  <li>Building a stronger NED profile, biography and positioning</li>
                  <li>Strengthening board papers, challenge and governance confidence</li>
                  <li>Navigating board dynamics, scrutiny and influence</li>
                </ul>
              </div>
              <div className="panel tinted-panel">
                <h3>What this helps you do</h3>
                <ul className="tick-list">
                  <li>Show up with greater authority and composure at board level</li>
                  <li>Think and communicate with stronger governance perspective</li>
                  <li>Move from operational depth to strategic contribution</li>
                  <li>Position yourself more credibly for portfolio or board opportunities</li>
                  <li>Bring challenge, judgement and value with confidence</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="insights" className="section light-surface">
          <div className="container">
            <div className="section-header centered">
              <div className="eyebrow">INSIGHTS</div>
              <h2>Thinking for leaders operating under pressure</h2>
              <p>
                A selection of themes that often sit behind the conversations senior leaders bring into coaching.
              </p>
            </div>

            <div className="insight-grid">
              <article className="insight-card">
                <div className="insight-tag">Leadership visibility</div>
                <h3>Performance is not always enough</h3>
                <p>
                  Many capable leaders assume their results will speak for themselves. At senior level, how your
                  performance is seen, understood and trusted often shapes what happens next.
                </p>
              </article>

              <article className="insight-card">
                <div className="insight-tag">Decision pressure</div>
                <h3>The cost of carrying leadership alone</h3>
                <p>
                  Senior roles can become isolating. Without a trusted space to think, leaders can over-carry,
                  under-share and make already difficult decisions harder than they need to be.
                </p>
              </article>

              <article className="insight-card">
                <div className="insight-tag">Board readiness</div>
                <h3>From functional leader to broader strategic voice</h3>
                <p>
                  Moving toward board or broader enterprise-level contribution requires a shift in presence,
                  perspective and communication — not simply more experience.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section id="contact" className="section contact-section">
          <div className="container">
            <div className="contact-panel">
              <div className="contact-copy">
                <div className="eyebrow">CONTACT</div>
                <h2>Senior leadership can be isolating. The right conversation can change everything.</h2>
                <p>
                  If you are navigating a high-stakes role, a complex transition, a board-related challenge, or simply
                  want a more experienced sounding board, get in touch for a confidential initial conversation.
                </p>
                <div className="contact-details">
                  <a href="mailto:derek@improveassociates.co.uk">derek@improveassociates.co.uk</a>
                  <a href="https://uk.linkedin.com/in/derekmcintyre/" target="_blank" rel="noreferrer">View Derek McIntyre on LinkedIn</a>
                  <a href="https://calendly.com/derekmcintyre72" target="_blank" rel="noreferrer">Schedule a conversation</a>
                </div>
              </div>

              <form className="contact-form" action="mailto:derek@improveassociates.co.uk" method="post" encType="text/plain">
                <label>
                  Name
                  <input type="text" name="name" placeholder="Your name" />
                </label>
                <label>
                  Email
                  <input type="email" name="email" placeholder="Your email" />
                </label>
                <label>
                  Organisation
                  <input type="text" name="organisation" placeholder="Organisation" />
                </label>
                <label>
                  Brief outline of your situation
                  <textarea name="message" rows="6" placeholder="A short note on your current challenge, transition or objective" />
                </label>
                <button type="submit" className="btn btn-primary full-width">Start the conversation</button>
              </form>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="container footer-wrap">
          <div>
            <div className="footer-title">Improve Associates Ltd</div>
            <div className="footer-subtitle">Executive Leadership Advisor &amp; Coach</div>
          </div>
          <div className="footer-note">Making your best . . . even better</div>
        </div>
      </footer>
    </div>
  );
}

const styles = {
  appShell: {
    background: "#f5f7f8",
    color: "#13203b",
    minHeight: "100vh",
    fontFamily: "Arial, Helvetica, sans-serif",
  },
};

const globalCss = `
  * { box-sizing: border-box; }
  html { scroll-behavior: smooth; }
  body { margin: 0; background: #f5f7f8; color: #13203b; }
  a { text-decoration: none; color: inherit; }

  .container {
    width: min(1180px, calc(100% - 40px));
    margin: 0 auto;
  }

  .site-header {
    position: sticky;
    top: 0;
    z-index: 1000;
    background: rgba(255,255,255,0.94);
    backdrop-filter: blur(8px);
    border-bottom: 1px solid #d8dde4;
  }

  .nav-wrap {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    min-height: 78px;
  }

  .brand-title {
    font-size: 1.7rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: #14203b;
  }

  .brand-subtitle {
    margin-top: 4px;
    font-size: 0.95rem;
    color: #607089;
  }

  .nav {
    display: flex;
    align-items: center;
    gap: 26px;
  }

  .nav a {
    font-size: 1rem;
    font-weight: 600;
    color: #44536a;
    padding: 8px 0;
    border-bottom: 2px solid transparent;
  }

  .nav a.active,
  .nav a:hover {
    color: #13203b;
    border-bottom-color: #15867b;
  }

  .menu-button {
    display: none;
    flex-direction: column;
    justify-content: center;
    gap: 5px;
    border: 0;
    background: transparent;
    cursor: pointer;
    padding: 8px;
  }

  .menu-button span {
    display: block;
    width: 24px;
    height: 2px;
    background: #13203b;
    border-radius: 2px;
  }

  .section {
    padding: 88px 0;
    scroll-margin-top: 95px;
  }

  .light-panel {
    background: linear-gradient(180deg, #eef2f4 0%, #f5f7f8 100%);
  }

  .light-surface {
    background: #eef3f2;
  }

  .hero-grid {
    display: grid;
    grid-template-columns: 1.2fr 0.9fr;
    gap: 36px;
    align-items: start;
  }

  .eyebrow {
    color: #15867b;
    letter-spacing: 0.16em;
    font-size: 0.86rem;
    font-weight: 700;
    margin-bottom: 16px;
  }

  .hero-title {
    margin: 0;
    font-size: clamp(2.7rem, 5vw, 4.6rem);
    line-height: 1.05;
    letter-spacing: -0.04em;
    color: #13203b;
  }

  .hero-title span {
    color: #15867b;
  }

  .hero-copy {
    margin: 28px 0 0;
    font-size: 1.35rem;
    line-height: 1.7;
    max-width: 900px;
    color: #495971;
  }

  .hero-cta-row {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    margin-top: 34px;
  }

  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 15px 24px;
    border-radius: 12px;
    font-weight: 700;
    transition: 0.2s ease;
    border: 1px solid transparent;
  }

  .btn:hover {
    transform: translateY(-1px);
  }

  .btn-primary {
    background: #13203b;
    color: #ffffff;
    box-shadow: 0 12px 24px rgba(19, 32, 59, 0.14);
  }

  .btn-secondary {
    background: #ffffff;
    color: #13203b;
    border-color: #d6dce3;
  }

  .hero-card,
  .panel,
  .service-card,
  .insight-card,
  .contact-panel {
    background: #ffffff;
    border: 1px solid #d9dfe5;
    border-radius: 24px;
    box-shadow: 0 8px 26px rgba(16, 31, 53, 0.05);
  }

  .hero-card {
    padding: 28px;
    background: #f6fbfa;
  }

  .hero-card-top {
    font-size: 1.35rem;
    font-weight: 700;
    margin-bottom: 18px;
  }

  .trust-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .trust-item {
    padding: 18px;
    background: #ffffff;
    border: 1px solid #dbe4e2;
    border-radius: 18px;
  }

  .stat {
    font-size: 1.5rem;
    font-weight: 700;
    color: #13203b;
    margin-bottom: 10px;
  }

  .trust-item p,
  .panel p,
  .section-header p,
  .contact-copy p,
  .insight-card p,
  .service-card p,
  .section-intro,
  .resource-card p {
    color: #4d5c73;
    line-height: 1.75;
    font-size: 1.08rem;
  }

  .panel {
    padding: 34px;
  }

  .panel-large {
    padding: 36px;
  }

  .panel h2,
  .section-header h2,
  .contact-copy h2 {
    margin: 0 0 14px;
    font-size: clamp(2rem, 3vw, 3rem);
    line-height: 1.15;
    letter-spacing: -0.03em;
  }

  .panel h3,
  .service-card h3,
  .insight-card h3,
  .soft-card h3,
  .resource-card h3 {
    margin: 0 0 12px;
    font-size: 1.55rem;
    line-height: 1.25;
  }

  .section-header {
    max-width: 900px;
    margin-bottom: 34px;
  }

  .section-header.centered {
    text-align: center;
    margin-left: auto;
    margin-right: auto;
  }

  .two-col-grid,
  .about-grid,
  .contact-panel {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }

  .soft-card {
    padding: 28px;
    background: #ffffff;
    border: 1px solid #d9dfe5;
    border-radius: 22px;
  }

  .soft-card.tinted,
  .tinted-panel {
    background: #f2f8f7;
  }

  .tick-list,
  .mini-list {
    padding-left: 20px;
    margin: 0;
    color: #41516a;
  }

  .tick-list li,
  .mini-list li {
    margin-bottom: 11px;
    line-height: 1.65;
    font-size: 1.04rem;
  }

  .service-grid,
  .insight-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 22px;
  }

  .resource-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 22px;
  }

  .service-card,
  .insight-card,
  .resource-card {
    padding: 28px;
    height: 100%;
  }

  .resource-card {
    background: #ffffff;
    border: 1px solid #d9dfe5;
    border-radius: 24px;
    box-shadow: 0 8px 26px rgba(16, 31, 53, 0.05);
  }

  .resource-tag,
  .insight-tag {
    display: inline-block;
    margin-bottom: 12px;
    font-size: 0.84rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #15867b;
  }

  .resource-btn {
    margin-top: 12px;
    width: 100%;
  }

  .contact-section {
    background: linear-gradient(180deg, #f5f7f8 0%, #eef2f4 100%);
  }

  .contact-panel {
    padding: 34px;
    align-items: start;
  }

  .contact-details {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-top: 24px;
  }

  .contact-details a {
    color: #13203b;
    font-weight: 700;
  }

  .contact-form {
    display: grid;
    gap: 16px;
  }

  .contact-form label {
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-weight: 700;
    color: #13203b;
    font-size: 0.98rem;
  }

  .contact-form input,
  .contact-form textarea {
    width: 100%;
    border: 1px solid #ccd4de;
    border-radius: 12px;
    padding: 14px 15px;
    font-size: 1rem;
    font-family: Arial, Helvetica, sans-serif;
    background: #ffffff;
    color: #13203b;
  }

  .contact-form textarea {
    resize: vertical;
    min-height: 140px;
  }

  .full-width {
    width: 100%;
  }

  .site-footer {
    border-top: 1px solid #d8dde4;
    background: #ffffff;
  }

  .footer-wrap {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
    padding: 28px 0;
  }

  .footer-title {
    font-size: 1.15rem;
    font-weight: 700;
  }

  .footer-subtitle,
  .footer-note {
    color: #677790;
    margin-top: 4px;
  }

  @media (max-width: 1100px) {
    .resource-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 1120px) {
    .hero-grid,
    .two-col-grid,
    .about-grid,
    .contact-panel,
    .service-grid,
    .insight-grid,
    .trust-grid {
      grid-template-columns: 1fr 1fr;
    }
  }

  @media (max-width: 900px) {
    .resource-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 860px) {
    .menu-button {
      display: flex;
    }

    .nav {
      display: none;
      position: absolute;
      top: 78px;
      left: 0;
      right: 0;
      background: #ffffff;
      border-bottom: 1px solid #d8dde4;
      padding: 18px 20px 20px;
      flex-direction: column;
      align-items: flex-start;
      gap: 14px;
      box-shadow: 0 14px 30px rgba(16, 31, 53, 0.08);
    }

    .nav.open {
      display: flex;
    }

    .hero-grid,
    .two-col-grid,
    .about-grid,
    .contact-panel,
    .service-grid,
    .insight-grid,
    .trust-grid {
      grid-template-columns: 1fr;
    }

    .section {
      padding: 72px 0;
    }

    .brand-title {
      font-size: 1.35rem;
    }

    .brand-subtitle {
      font-size: 0.87rem;
    }

    .hero-copy {
      font-size: 1.12rem;
    }

    .footer-wrap {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  @media (max-width: 560px) {
    .container {
      width: min(100% - 28px, 1180px);
    }

    .site-header {
      position: sticky;
    }

    .nav-wrap {
      min-height: 72px;
    }

    .section {
      padding: 60px 0;
      scroll-margin-top: 82px;
    }

    .hero-title {
      font-size: 2.45rem;
      line-height: 1.08;
    }

    .hero-copy,
    .trust-item p,
    .panel p,
    .section-header p,
    .contact-copy p,
    .insight-card p,
    .service-card p,
    .section-intro,
    .tick-list li,
    .mini-list li,
    .resource-card p {
      font-size: 1rem;
      line-height: 1.65;
    }

    .panel,
    .panel-large,
    .service-card,
    .insight-card,
    .resource-card,
    .contact-panel,
    .hero-card,
    .soft-card {
      padding: 22px;
      border-radius: 20px;
    }

    .btn {
      width: 100%;
    }

    .hero-cta-row {
      flex-direction: column;
    }
  }
`;
